import {
  ANALYTICAL_TASK_SCHEMA_VERSION,
  type AnalyticalCheckpoint,
  type AnalyticalCheckpointScope,
  type AnalyticalDimension,
  type AnalyticalPosition,
  type AnalyticalSubject,
  type AnalyticalSubjectKind,
  type AnalyticalTaskSet,
  type AnalyticalTaskState,
  type AnalyticalWorkstream,
  type CharacterIndexPlanScope,
  type CharacterIndexTaskSetEnrollment,
} from '../types/analyticalTaskTypes'

export const CHARACTER_ANALYTICAL_DIMENSIONS: readonly AnalyticalDimension[] = [
  'identity',
  'coreference',
  'characterization',
  'focalization',
  'speaker',
  'review',
]

export const CHARACTER_INDEX_PRODUCT_DIMENSIONS: readonly AnalyticalDimension[] = [
  'cast',
  'sections',
  'review',
]

const unique = <T,>(values: readonly T[]): T[] => Array.from(new Set(values))

const createId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const subjectIdFor = (kind: AnalyticalSubjectKind, externalId: string): string => `${kind}:${externalId}`

const workstreamIdFor = (
  chapterId: string,
  witnessId: string,
  subjectId: string,
  dimension: AnalyticalDimension,
): string => `workstream:${chapterId}:${witnessId}:${subjectId}:${dimension}`

const nextTaskState = (current: AnalyticalTaskState, requested: AnalyticalTaskState): AnalyticalTaskState => (
  current === requested ? current : requested
)

const touchTaskSet = (taskSet: AnalyticalTaskSet, now: string): AnalyticalTaskSet => ({
  ...taskSet,
  updatedAt: now,
})

const applyWorkstreamState = (
  workstream: AnalyticalWorkstream,
  state: AnalyticalTaskState,
  now: string,
): AnalyticalWorkstream => ({
  ...workstream,
  state: nextTaskState(workstream.state, state),
  updatedAt: now,
  ...(state === 'in_progress' && !workstream.startedAt ? { startedAt: now } : {}),
  ...(state === 'in_review' ? { reviewAt: now } : {}),
  ...(state === 'done' ? { completedAt: now } : {}),
  ...(state !== 'in_review' ? { reviewAt: undefined } : {}),
  ...(state !== 'done' ? { completedAt: undefined } : {}),
})

const buildSubject = ({
  kind,
  externalId,
  label,
  metadata,
  now,
}: {
  kind: AnalyticalSubjectKind
  externalId: string
  label: string
  metadata?: Record<string, unknown>
  now: string
}): AnalyticalSubject => ({
  id: subjectIdFor(kind, externalId),
  kind,
  externalId,
  label,
  ...(metadata ? { metadata } : {}),
  createdAt: now,
  updatedAt: now,
})

const buildWorkstream = ({
  chapterId,
  dimension,
  now,
  order,
  subjectId,
  witnessId,
}: {
  chapterId: string
  dimension: AnalyticalDimension
  now: string
  order: number
  subjectId: string
  witnessId: string
}): AnalyticalWorkstream => ({
  id: workstreamIdFor(chapterId, witnessId, subjectId, dimension),
  subjectId,
  dimension,
  state: 'not_started',
  chapterId,
  witnessId,
  order,
  createdAt: now,
  updatedAt: now,
})

export const createCharacterIndexTaskSet = ({
  id = createId('character-index'),
  title,
  enrollments,
  now = new Date().toISOString(),
  metadata,
  scope,
}: {
  id?: string
  title: string
  enrollments: CharacterIndexTaskSetEnrollment[]
  now?: string
  metadata?: Record<string, unknown>
  scope?: CharacterIndexPlanScope
}): AnalyticalTaskSet => {
  const productSubject = buildSubject({
    kind: 'product',
    externalId: id,
    label: title,
    now,
  })
  const workstreams = enrollments.flatMap(enrollment => (
    CHARACTER_INDEX_PRODUCT_DIMENSIONS.map((dimension, order) => buildWorkstream({
      chapterId: enrollment.chapterId,
      dimension,
      now,
      order,
      subjectId: productSubject.id,
      witnessId: enrollment.witnessId,
    }))
  ))
  return {
    id,
    schemaVersion: ANALYTICAL_TASK_SCHEMA_VERSION,
    productKind: 'character-index',
    title,
    state: 'not_started',
    scope: scope || characterIndexScopeFromEnrollments(enrollments),
    enrollments: enrollments.map(enrollment => ({
      ...enrollment,
      readiness: { ...enrollment.readiness },
    })),
    workIds: unique(enrollments.map(enrollment => enrollment.workId)),
    chapterIds: unique(enrollments.map(enrollment => enrollment.chapterId)),
    witnessIds: unique(enrollments.map(enrollment => enrollment.witnessId)),
    subjects: [productSubject],
    workstreams,
    checkpoints: [],
    createdAt: now,
    updatedAt: now,
    ...(metadata ? { metadata } : {}),
  }
}

const enrollmentKey = (enrollment: CharacterIndexTaskSetEnrollment): string =>
  `${enrollment.workId}::${enrollment.chapterId}::${enrollment.witnessId}`

export const characterIndexScopeFromEnrollments = (
  enrollments: CharacterIndexTaskSetEnrollment[],
): CharacterIndexPlanScope => {
  const works = new Map<string, CharacterIndexPlanScope['works'][number]>()
  enrollments.forEach(enrollment => {
    const work = works.get(enrollment.workId) || {
      workId: enrollment.workId,
      workLabel: enrollment.workLabel,
      sections: [],
    }
    if (!work.sections.some(section => section.sectionId === enrollment.chapterId)) {
      work.sections.push({
        sectionId: enrollment.chapterId,
        sectionLabel: enrollment.chapterLabel || enrollment.chapterId,
      })
    }
    works.set(enrollment.workId, work)
  })
  return { works: Array.from(works.values()) }
}

const mergeCharacterIndexScope = (
  scope: CharacterIndexPlanScope,
  enrollment: CharacterIndexTaskSetEnrollment,
): CharacterIndexPlanScope => {
  const works = scope.works.map(work => ({
    ...work,
    sections: work.sections.map(section => ({ ...section })),
  }))
  const existing = works.find(work => work.workId === enrollment.workId)
  if (!existing) {
    works.push(characterIndexScopeFromEnrollments([enrollment]).works[0])
  } else if (!existing.sections.some(section => section.sectionId === enrollment.chapterId)) {
    existing.sections.push({
      sectionId: enrollment.chapterId,
      sectionLabel: enrollment.chapterLabel || enrollment.chapterId,
    })
  }
  return { works }
}

/** Upgrade persisted v1 held-scope plans without discarding operational ids or history. */
export const migrateAnalyticalTaskSet = (
  taskSet: AnalyticalTaskSet | Record<string, any>,
): AnalyticalTaskSet => {
  if (taskSet.schemaVersion === ANALYTICAL_TASK_SCHEMA_VERSION && taskSet.scope) {
    return taskSet as AnalyticalTaskSet
  }
  const enrollments = Array.isArray(taskSet.enrollments) ? taskSet.enrollments : []
  const migratedEnrollments = enrollments.map((enrollment: CharacterIndexTaskSetEnrollment) => ({
    ...enrollment,
    sourceKind: enrollment.sourceKind || 'held' as const,
    operationalWorkId: enrollment.operationalWorkId || enrollment.workId,
    operationalChapterId: enrollment.operationalChapterId || enrollment.chapterId,
  }))
  return {
    ...taskSet,
    schemaVersion: ANALYTICAL_TASK_SCHEMA_VERSION,
    scope: taskSet.scope || characterIndexScopeFromEnrollments(migratedEnrollments),
    enrollments: migratedEnrollments,
  } as AnalyticalTaskSet
}

export const enrollCharacterIndexWitness = ({
  taskSet,
  enrollment,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  enrollment: CharacterIndexTaskSetEnrollment
  now?: string
}): AnalyticalTaskSet => {
  const key = enrollmentKey(enrollment)
  const existing = taskSet.enrollments.find(candidate => enrollmentKey(candidate) === key)
  const enrollments = existing
    ? taskSet.enrollments.map(candidate => enrollmentKey(candidate) === key ? {
        ...enrollment,
        readiness: { ...enrollment.readiness },
      } : candidate)
    : [...taskSet.enrollments, { ...enrollment, readiness: { ...enrollment.readiness } }]
  const productSubject = taskSet.subjects.find(subject => subject.kind === 'product')
  if (!productSubject) {
    throw new Error('A Character Index task set requires its product subject.')
  }
  const existingDimensions = new Set(taskSet.workstreams
    .filter(stream => (
      stream.subjectId === productSubject.id &&
      stream.witnessId === enrollment.witnessId &&
      stream.chapterId === enrollment.chapterId
    ))
    .map(stream => stream.dimension))
  const additions = CHARACTER_INDEX_PRODUCT_DIMENSIONS.flatMap((dimension, order): AnalyticalWorkstream[] => (
    existingDimensions.has(dimension)
      ? []
      : [buildWorkstream({
          chapterId: enrollment.chapterId,
          dimension,
          now,
          order,
          subjectId: productSubject.id,
          witnessId: enrollment.witnessId,
        })]
  ))
  return touchTaskSet({
    ...taskSet,
    scope: mergeCharacterIndexScope(taskSet.scope, enrollment),
    enrollments,
    workIds: unique(enrollments.map(candidate => candidate.workId)),
    chapterIds: unique(enrollments.map(candidate => candidate.chapterId)),
    witnessIds: unique(enrollments.map(candidate => candidate.witnessId)),
    workstreams: [...taskSet.workstreams, ...additions],
  }, now)
}

export const ensureAnalyticalSubjectWorkstreams = ({
  taskSet,
  subject,
  chapterId,
  witnessId,
  dimensions,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  subject: {
    kind: AnalyticalSubjectKind
    externalId: string
    label: string
    metadata?: Record<string, unknown>
  }
  chapterId: string
  witnessId: string
  dimensions: readonly AnalyticalDimension[]
  now?: string
}): {
  taskSet: AnalyticalTaskSet
  subjectId: string
  workstreamIds: Record<string, string>
} => {
  const id = subjectIdFor(subject.kind, subject.externalId)
  const existingSubject = taskSet.subjects.find(candidate => candidate.id === id)
  const subjects = existingSubject
    ? taskSet.subjects.map(candidate => candidate.id === id ? {
        ...candidate,
        label: subject.label,
        metadata: subject.metadata || candidate.metadata,
        updatedAt: now,
      } : candidate)
    : [...taskSet.subjects, buildSubject({ ...subject, now })]
  const existingByDimension = new Map(taskSet.workstreams
    .filter(stream => (
      stream.subjectId === id &&
      stream.witnessId === witnessId &&
      stream.chapterId === chapterId
    ))
    .map(stream => [stream.dimension, stream]))
  const additions = dimensions.flatMap((dimension, order): AnalyticalWorkstream[] => (
    existingByDimension.has(dimension)
      ? []
      : [buildWorkstream({ chapterId, dimension, now, order, subjectId: id, witnessId })]
  ))
  const workstreams = [...taskSet.workstreams, ...additions]
  const workstreamIds = Object.fromEntries(dimensions.map(dimension => [
    dimension,
    existingByDimension.get(dimension)?.id || workstreamIdFor(chapterId, witnessId, id, dimension),
  ]))
  return {
    taskSet: touchTaskSet({ ...taskSet, subjects, workstreams }, now),
    subjectId: id,
    workstreamIds,
  }
}

export const getAnalyticalSubjectWorkPackage = (
  taskSet: AnalyticalTaskSet,
  subjectId: string,
  witnessId: string,
): AnalyticalWorkstream[] => taskSet.workstreams
  .filter(stream => stream.subjectId === subjectId && stream.witnessId === witnessId && !stream.archivedAt)
  .slice()
  .sort((left, right) => left.order - right.order || left.dimension.localeCompare(right.dimension))

export const createAnalyticalCheckpoint = ({
  taskSet,
  id = createId('checkpoint'),
  title,
  workstreamIds,
  scope,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  id?: string
  title?: string
  workstreamIds: string[]
  scope: AnalyticalCheckpointScope
  now?: string
}): AnalyticalTaskSet => {
  const selected = workstreamIds.map(id => taskSet.workstreams.find(stream => stream.id === id))
    .filter((stream): stream is AnalyticalWorkstream => Boolean(stream && !stream.archivedAt))
  if (selected.length === 0) {
    throw new Error('A checkpoint requires at least one active analytical workstream.')
  }
  const selectedIds = unique(selected.map(stream => stream.id))
  const checkpoint: AnalyticalCheckpoint = {
    id,
    ...(title?.trim() ? { title: title.trim() } : {}),
    state: 'in_progress',
    workstreamIds: selectedIds,
    subjectIds: unique(selected.map(stream => stream.subjectId)),
    dimensions: unique(selected.map(stream => stream.dimension)),
    scope: {
      ...scope,
      start: { ...scope.start },
      end: scope.end ? { ...scope.end } : undefined,
      resume: { ...scope.resume },
    },
    createdAt: now,
    updatedAt: now,
  }
  const workstreams = taskSet.workstreams.map(stream => (
    selectedIds.includes(stream.id) && stream.state === 'not_started'
      ? applyWorkstreamState(stream, 'in_progress', now)
      : stream
  ))
  const existingIndex = taskSet.checkpoints.findIndex(candidate => candidate.id === id)
  const checkpoints = existingIndex >= 0
    ? taskSet.checkpoints.map(candidate => candidate.id === id ? checkpoint : candidate)
    : [...taskSet.checkpoints, checkpoint]
  return touchTaskSet({
    ...taskSet,
    state: taskSet.state === 'not_started' ? 'in_progress' : taskSet.state,
    workstreams,
    checkpoints,
  }, now)
}

export const addWorkstreamsToAnalyticalCheckpoint = ({
  taskSet,
  checkpointId,
  workstreamIds,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  checkpointId: string
  workstreamIds: string[]
  now?: string
}): AnalyticalTaskSet => {
  const checkpoint = taskSet.checkpoints.find(candidate => candidate.id === checkpointId)
  if (!checkpoint) {
    throw new Error(`Analytical checkpoint ${checkpointId} was not found.`)
  }
  const additions = workstreamIds.map(id => taskSet.workstreams.find(stream => stream.id === id))
    .filter((stream): stream is AnalyticalWorkstream => Boolean(stream && !stream.archivedAt))
  const nextIds = unique([...checkpoint.workstreamIds, ...additions.map(stream => stream.id)])
  const selected = nextIds.map(id => taskSet.workstreams.find(stream => stream.id === id))
    .filter((stream): stream is AnalyticalWorkstream => Boolean(stream))
  return touchTaskSet({
    ...taskSet,
    state: taskSet.state === 'not_started' ? 'in_progress' : taskSet.state,
    workstreams: taskSet.workstreams.map(stream => (
      additions.some(candidate => candidate.id === stream.id) && stream.state === 'not_started'
        ? applyWorkstreamState(stream, 'in_progress', now)
        : stream
    )),
    checkpoints: taskSet.checkpoints.map(candidate => candidate.id === checkpointId ? {
      ...candidate,
      state: candidate.state === 'not_started' ? 'in_progress' : candidate.state,
      workstreamIds: nextIds,
      subjectIds: unique(selected.map(stream => stream.subjectId)),
      dimensions: unique(selected.map(stream => stream.dimension)),
      updatedAt: now,
    } : candidate),
  }, now)
}

export const recordCharacterAnalyticalActivity = ({
  taskSet,
  character,
  chapterId,
  witnessId,
  dimension,
  position,
  now = new Date().toISOString(),
  checkpointId,
  activeCheckpointId,
}: {
  taskSet: AnalyticalTaskSet
  character: { id: string; label: string; metadata?: Record<string, unknown> }
  chapterId: string
  witnessId: string
  dimension: Extract<AnalyticalDimension, 'identity' | 'coreference' | 'characterization' | 'focalization' | 'speaker' | 'review'>
  position: AnalyticalPosition
  now?: string
  checkpointId?: string
  activeCheckpointId?: string
}): { taskSet: AnalyticalTaskSet; subjectId: string; workstreamId: string; checkpointId?: string } => {
  const ensured = ensureAnalyticalSubjectWorkstreams({
    taskSet,
    subject: {
      kind: 'character',
      externalId: character.id,
      label: character.label,
      metadata: character.metadata,
    },
    chapterId,
    witnessId,
    dimensions: CHARACTER_ANALYTICAL_DIMENSIONS,
    now,
  })
  const workstreamId = ensured.workstreamIds[dimension]
  let next = touchTaskSet({
    ...ensured.taskSet,
    state: ensured.taskSet.state === 'not_started' ? 'in_progress' : ensured.taskSet.state,
    workstreams: ensured.taskSet.workstreams.map(stream => stream.id === workstreamId ? {
      ...(stream.state === 'not_started' ? applyWorkstreamState(stream, 'in_progress', now) : stream),
      lastActivityAt: now,
      lastPosition: { ...position },
      updatedAt: now,
    } : stream),
  }, now)
  const targetCheckpointId = activeCheckpointId || checkpointId
  const existingCheckpoint = targetCheckpointId
    ? next.checkpoints.find(candidate => candidate.id === targetCheckpointId)
    : undefined
  if (existingCheckpoint) {
    next = addWorkstreamsToAnalyticalCheckpoint({
      taskSet: next,
      checkpointId: existingCheckpoint.id,
      workstreamIds: [workstreamId],
      now,
    })
    next = {
      ...next,
      checkpoints: next.checkpoints.map(candidate => candidate.id === existingCheckpoint.id ? {
        ...candidate,
        scope: { ...candidate.scope, resume: { ...position } },
        updatedAt: now,
      } : candidate),
    }
  } else if (dimension === 'coreference') {
    const generatedCheckpointId = checkpointId || createId('checkpoint-coreference')
    next = createAnalyticalCheckpoint({
      taskSet: next,
      id: generatedCheckpointId,
      workstreamIds: [workstreamId],
      scope: {
        chapterId,
        witnessId,
        start: { ...position },
        resume: { ...position },
      },
      now,
    })
    return { taskSet: next, subjectId: ensured.subjectId, workstreamId, checkpointId: generatedCheckpointId }
  }
  return {
    taskSet: next,
    subjectId: ensured.subjectId,
    workstreamId,
    ...(targetCheckpointId ? { checkpointId: targetCheckpointId } : {}),
  }
}

export const setAnalyticalWorkstreamState = ({
  taskSet,
  workstreamId,
  state,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  workstreamId: string
  state: AnalyticalTaskState
  now?: string
}): AnalyticalTaskSet => {
  if (!taskSet.workstreams.some(stream => stream.id === workstreamId)) {
    throw new Error(`Analytical workstream ${workstreamId} was not found.`)
  }
  return touchTaskSet({
    ...taskSet,
    workstreams: taskSet.workstreams.map(stream => (
      stream.id === workstreamId ? applyWorkstreamState(stream, state, now) : stream
    )),
  }, now)
}

export const setAnalyticalTaskSetState = ({
  taskSet,
  state,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  state: AnalyticalTaskState
  now?: string
}): AnalyticalTaskSet => ({
  ...taskSet,
  state,
  updatedAt: now,
  ...(state === 'in_review' ? { reviewAt: now } : { reviewAt: undefined }),
  ...(state === 'done' ? { completedAt: now } : { completedAt: undefined }),
})

export const setAnalyticalCheckpointState = ({
  taskSet,
  checkpointId,
  state,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  checkpointId: string
  state: AnalyticalTaskState
  now?: string
}): AnalyticalTaskSet => {
  if (!taskSet.checkpoints.some(checkpoint => checkpoint.id === checkpointId)) {
    throw new Error(`Analytical checkpoint ${checkpointId} was not found.`)
  }
  return touchTaskSet({
    ...taskSet,
    checkpoints: taskSet.checkpoints.map(checkpoint => checkpoint.id === checkpointId ? {
      ...checkpoint,
      state,
      updatedAt: now,
      ...(state === 'in_review' ? { reviewAt: now } : { reviewAt: undefined }),
      ...(state === 'done' ? { completedAt: now } : { completedAt: undefined }),
    } : checkpoint),
  }, now)
}

const stateRank: Record<AnalyticalTaskState, number> = {
  not_started: 0,
  in_progress: 1,
  in_review: 2,
  done: 3,
}

export const mergeAnalyticalSubjects = ({
  taskSet,
  sourceSubjectId,
  receivingSubjectId,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  sourceSubjectId: string
  receivingSubjectId: string
  now?: string
}): AnalyticalTaskSet => {
  if (sourceSubjectId === receivingSubjectId) return taskSet
  const source = taskSet.subjects.find(subject => subject.id === sourceSubjectId)
  const receiver = taskSet.subjects.find(subject => subject.id === receivingSubjectId)
  if (!source || !receiver) {
    throw new Error('Both analytical subjects must exist before they can be merged.')
  }
  const workstreams = taskSet.workstreams.slice()
  const streamReplacement = new Map<string, string>()
  taskSet.workstreams.filter(stream => stream.subjectId === sourceSubjectId && !stream.archivedAt).forEach(sourceStream => {
    const receivingIndex = workstreams.findIndex(stream => (
      stream.subjectId === receivingSubjectId &&
      stream.chapterId === sourceStream.chapterId &&
      stream.witnessId === sourceStream.witnessId &&
      stream.dimension === sourceStream.dimension &&
      !stream.archivedAt
    ))
    const receivingId = receivingIndex >= 0
      ? workstreams[receivingIndex].id
      : workstreamIdFor(
          sourceStream.chapterId,
          sourceStream.witnessId,
          receivingSubjectId,
          sourceStream.dimension,
        )
    if (receivingIndex >= 0) {
      const receivingStream = workstreams[receivingIndex]
      const preferredState = stateRank[sourceStream.state] > stateRank[receivingStream.state]
        ? sourceStream.state
        : receivingStream.state
      workstreams[receivingIndex] = {
        ...applyWorkstreamState(receivingStream, preferredState, now),
        lastActivityAt: [sourceStream.lastActivityAt, receivingStream.lastActivityAt]
          .filter((value): value is string => Boolean(value))
          .sort()
          .slice(-1)[0],
        lastPosition: sourceStream.lastActivityAt && (!receivingStream.lastActivityAt || sourceStream.lastActivityAt > receivingStream.lastActivityAt)
          ? sourceStream.lastPosition
          : receivingStream.lastPosition,
      }
    } else {
      workstreams.push({
        ...sourceStream,
        id: receivingId,
        subjectId: receivingSubjectId,
        updatedAt: now,
      })
    }
    streamReplacement.set(sourceStream.id, receivingId)
  })
  const migrated = workstreams.map(stream => stream.subjectId === sourceSubjectId ? {
    ...stream,
    archivedAt: now,
    mergedIntoWorkstreamId: streamReplacement.get(stream.id),
    updatedAt: now,
  } : stream)
  const checkpoints = taskSet.checkpoints.map(checkpoint => {
    const workstreamIds = unique(checkpoint.workstreamIds.map(id => streamReplacement.get(id) || id))
    const subjectIds = unique(checkpoint.subjectIds.map(id => id === sourceSubjectId ? receivingSubjectId : id))
    const dimensions = unique(workstreamIds.map(id => migrated.find(stream => stream.id === id)?.dimension)
      .filter((dimension): dimension is AnalyticalDimension => Boolean(dimension)))
    return { ...checkpoint, workstreamIds, subjectIds, dimensions, updatedAt: now }
  })
  return touchTaskSet({
    ...taskSet,
    subjects: taskSet.subjects.map(subject => subject.id === sourceSubjectId ? {
      ...subject,
      archivedAt: now,
      mergedIntoSubjectId: receivingSubjectId,
      updatedAt: now,
    } : subject),
    workstreams: migrated,
    checkpoints,
  }, now)
}

export const archiveAnalyticalSubject = ({
  taskSet,
  subjectId,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  subjectId: string
  now?: string
}): AnalyticalTaskSet => touchTaskSet({
  ...taskSet,
  subjects: taskSet.subjects.map(subject => subject.id === subjectId ? {
    ...subject,
    archivedAt: now,
    updatedAt: now,
  } : subject),
  workstreams: taskSet.workstreams.map(stream => stream.subjectId === subjectId ? {
    ...stream,
    archivedAt: now,
    updatedAt: now,
  } : stream),
  }, now)

export const renameAnalyticalSubject = ({
  taskSet,
  subjectId,
  label,
  now = new Date().toISOString(),
}: {
  taskSet: AnalyticalTaskSet
  subjectId: string
  label: string
  now?: string
}): AnalyticalTaskSet => {
  const nextLabel = label.trim()
  if (!nextLabel || !taskSet.subjects.some(subject => subject.id === subjectId)) {
    return taskSet
  }
  return touchTaskSet({
    ...taskSet,
    subjects: taskSet.subjects.map(subject => subject.id === subjectId ? {
      ...subject,
      label: nextLabel,
      updatedAt: now,
    } : subject),
  }, now)
}
